import React, {useRef, useState} from "react"
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	RadioGroup,
	FormControlLabel,
	Radio,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Alert,
	Divider,
	TextField,
} from "@mui/material"

import {NoteForm, NoteFormHandle} from "../NoteForm"

import {api} from "@indebted/api"
import type {ScaffoldCaseResponse} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

const CUSTOM_DATE_VALUE = "CUSTOM_DATE"

type OpenCaseModalProps = {
	customerID: string
	scaffoldData: ScaffoldCaseResponse
	onClose: () => void
	onSubmitted: () => void
}

function OpenCaseModal({customerID, scaffoldData, onClose, onSubmitted}: OpenCaseModalProps) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const noteFormRef = useRef<NoteFormHandle>(null)

	const expiryConfig = scaffoldData.ExpiryConfig
	const defaultDuration = expiryConfig ? expiryConfig.DefaultExpiryDuration.ID : ""
	const [selectedDuration, setSelectedDuration] = useState<string>(defaultDuration)
	const [customDate, setCustomDate] = useState<string>("")
	const [submitting, setSubmitting] = useState(false)
	const [uploading, setUploading] = useState(false)

	const accounts = scaffoldData.Accounts ?? []
	const hasAccounts = accounts.length > 0
	const singleAccount = accounts.length === 1
	const [selectedAccountIDs, setSelectedAccountIDs] = useState<string[]>(singleAccount ? [accounts[0].AccountID] : [])

	const toggleAccount = (accountID: string) => {
		setSelectedAccountIDs((prev) =>
			prev.includes(accountID) ? prev.filter((id) => id !== accountID) : [...prev, accountID],
		)
	}

	const isCustomDate = selectedDuration === CUSTOM_DATE_VALUE
	const expiryDurationToSubmit = isCustomDate ? customDate : selectedDuration || undefined

	const noteForms = scaffoldData.NoteForms.map((f) => ({
		Type: f.Type,
		Label: f.Label,
		Version: f.Version,
		Fields: f.Fields.map((field) => ({
			Name: field.Name,
			Type: field.Type,
			Required: field.Required,
			Options: field.Options,
		})),
	}))

	const handleSubmit = async () => {
		if (submitting) return

		if (isCustomDate && !customDate) {
			notification.error("Please select a date")
			return
		}

		if (hasAccounts && selectedAccountIDs.length === 0) {
			notification.error("Please select at least one account")
			return
		}

		setSubmitting(true)

		try {
			const noteID = await noteFormRef.current?.createNote()
			if (!noteID) {
				notification.error("Please fill in the required note")
				setSubmitting(false)
				return
			}

			await api.customer.submitCase(customerID, scaffoldData.CaseID, {
				CaseType: scaffoldData.CaseType,
				AccountIDs: hasAccounts ? selectedAccountIDs : undefined,
				NoteID: noteID,
				ExpiryDuration: expiryDurationToSubmit,
				AgentName: agentName,
				AgentEmail: agentEmail,
			})

			notification.success("Case opened successfully")
			onSubmitted()
		} catch (error: any) {
			notification.error(error.Message || "Failed to open case")
		} finally {
			setSubmitting(false)
		}
	}

	const todayStr = new Date().toISOString().split("T")[0]

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogContent>
				<Box textAlign="center" mb={3} mt={1}>
					<Typography variant="h6" fontWeight={600} color={colors.endlessGalaxy["800"]} mb={0.5}>
						Open Case
					</Typography>
					<Chip label={scaffoldData.CaseTypeLabel} size="small" variant="outlined" sx={{mt: 1}} />
				</Box>
				<Divider sx={{mb: 2}} />

				{hasAccounts && (
					<>
						<Typography variant="subtitle1" fontWeight={600} mb={1}>
							Accounts
						</Typography>
						{!singleAccount && (
							<Typography variant="body2" color="textSecondary" mb={1}>
								Select at least one account
							</Typography>
						)}
						<Box sx={{mb: 2, display: "flex", flexDirection: "column", gap: 1}}>
							{accounts.map((a) => {
								const selected = selectedAccountIDs.includes(a.AccountID)
								return (
									<Box
										key={a.AccountID}
										onClick={singleAccount ? undefined : () => toggleAccount(a.AccountID)}
										sx={{
											display: "flex",
											alignItems: "center",
											border:
												selected && !singleAccount
													? `1px solid ${colors.warmBlue["400"]}`
													: "none",
											borderRadius: 2,
											boxShadow: 2,
											p: 2,
											cursor: singleAccount ? "default" : "pointer",
											backgroundColor:
												selected && !singleAccount ? colors.warmBlue["50"] : "white",
										}}
									>
										{!singleAccount && (
											<Checkbox
												checked={selected}
												size="small"
												sx={{
													p: 0,
													mr: 1.5,
													color: colors.gray["400"],
													"&.Mui-checked": {color: colors.warmBlue["700"]},
												}}
											/>
										)}
										<Box>
											<Typography variant="body2" fontWeight={600}>
												{a.AccountRef}
											</Typography>
											<Typography variant="body2" color="textSecondary">
												{a.Balance}
											</Typography>
										</Box>
									</Box>
								)
							})}
						</Box>
					</>
				)}

				{expiryConfig && (
					<>
						<Typography variant="subtitle1" fontWeight={600} mb={1}>
							Case Expiration
						</Typography>
						<Box
							sx={{
								backgroundColor: colors.gray["50"],
								border: `1px solid ${colors.gray["200"]}`,
								borderRadius: 2,
								p: 2.5,
								mb: 2,
							}}
						>
							<RadioGroup value={selectedDuration} onChange={(e) => setSelectedDuration(e.target.value)}>
								{expiryConfig.AllowedDurations.map((d) => (
									<FormControlLabel
										key={d.ID}
										value={d.ID}
										control={<Radio size="small" />}
										label={<Typography variant="body2">{d.Display}</Typography>}
										sx={{ml: 0}}
									/>
								))}
								{expiryConfig.AllowSpecificExpiryDate && (
									<FormControlLabel
										value={CUSTOM_DATE_VALUE}
										control={<Radio size="small" />}
										label={<Typography variant="body2">Custom date</Typography>}
										sx={{ml: 0}}
									/>
								)}
							</RadioGroup>

							{isCustomDate && (
								<TextField
									type="date"
									label="Select date"
									size="small"
									fullWidth
									value={customDate}
									onChange={(e) => setCustomDate(e.target.value)}
									slotProps={{inputLabel: {shrink: true}, htmlInput: {min: todayStr}}}
									sx={{mt: 1}}
								/>
							)}
						</Box>

						{selectedDuration !== defaultDuration && (
							<Alert severity="warning" sx={{mb: 2}}>
								Please note the reason for overriding the default expiry for this Case Type.
							</Alert>
						)}
					</>
				)}

				<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
					Submission Notes
				</Typography>
				<Typography variant="body2" color="textSecondary" mb={1}>
					Please provide the reason for this case submission.
				</Typography>
				<NoteForm
					ref={noteFormRef}
					customerID={customerID}
					forms={noteForms}
					attachmentsEnabled
					showSubmitButton={false}
					disabled={submitting}
					onUploadingChange={setUploading}
					size="small"
					pending
				/>

				<Alert severity="warning" sx={{mt: 2, borderRadius: 2}} icon={false}>
					<Typography variant="body2" color={colors.warning["700"]}>
						Opening this case will automatically pause all collection activity for this customer.
					</Typography>
				</Alert>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 3, pt: 2}}>
				<Button
					onClick={onClose}
					variant="outlined"
					disabled={submitting || uploading}
					sx={{borderRadius: 2, color: colors.warmBlue["900"], borderColor: colors.warmBlue["900"]}}
				>
					CANCEL
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={submitting || uploading}
					sx={{
						borderRadius: 2,
						backgroundColor: colors.warmBlue["900"],
						"&:hover": {backgroundColor: colors.warmBlue["800"]},
					}}
				>
					{submitting ? <CircularProgress size={20} color="inherit" /> : "OPEN CASE"}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export {OpenCaseModal}
